<template>
  <div class="checkout-step-three">

    <div class="checkout-step__header">
      <div class="row align-items-end">
        <div class="col-12 col-md-9">
          <h3 class="checkout-step__headline">{{ $t('pages.checkoutStepThree.title') }}</h3>
          <p class="checkout-step__copy">{{ $t('pages.checkoutStepThree.copy') }}</p>
        </div>
        <div class="col-12 col-md-3">
          <small class="checkout-step__copy checkout-step__copy--small">
            {{ $t('generic.priceInfo') }}
          </small>
        </div>
      </div>
    </div>

    <CiInfoBox :type="'warning'" v-if="getMandatoryExtrasPriceZero && getMandatoryExtrasPriceZero.length > 0">
      <!--<h4>{{ $t('pages.checkoutStepThree.infoBox.title') }}</h4>-->
      <p>{{ $t('pages.checkoutStepThree.infoBox.copy') }}</p>
      <div class="wrapper d-flex justify-content-between" v-for="mandatory in getMandatoryExtrasPriceZero" :key="mandatory.object_id">
        <div class="label" v-html="mandatory.description"></div>
        <div class="label">
          <span class="d-block" v-if="mandatory.price !== 0">{{ mandatory.price | currency(campground.currency, language.code) }}</span>
          <span class="d-block" v-if="mandatory.currency_alt && mandatory.currency_alt.price !== 0">{{ mandatory.currency_alt.price | currency(currency.code, language.code) }}</span>
        </div>
      </div>
    </CiInfoBox>

    <div class="extra-selector">
      <div class="row">
        <div class="col-12 col-md-6 col-lg-12" v-for="extra in getMandatoryExtrasPriceNotZero" :key="extra.id">
          <div class="extra">
            <CiSelectionBox
              :class="{ clickable: false }"
              :active="true"
              :disabled="true">
              <div slot="left" class="extra__status">
                <span class="warning">
                  {{ $tc('generic.labels.mandatory_extra', 1) }}
                  <iconLock class="icon"></iconLock>
                </span>
              </div>
              <div slot="middle" class="extra__meta">
                <h3 class="meta__headline">{{ extra.name }}</h3>
                <div class="meta__description" v-html="extra.description"></div>
              </div>
              <div slot="right" class="extra__price">
                <span class="price__value"> {{ extra.price | currency(campground.currency, language.code) }}</span>
                <span class="price__value price__value--alt" v-if="extra.currency_alt">{{ extra.currency_alt.price | currency(currency.code, language.code) }}</span>
              </div>
            </CiSelectionBox>
          </div>
        </div>
        <div class="col-12 col-md-6 col-lg-12" v-for="extra in getAvailableExtras" :key="extra.id">
          <div class="extra">
            <CiSelectionBox
              :class="{ clickable: extra.extra_type !== 'free'}"
              :active="getExtraValue(extra) === true || getExtraValue(extra) > 0"
              @click.stop.native="extra.extra_type !== 'free' && onExtraClicked(extra, $event)">
              <div slot="left" class="extra__status">
                <CiSwitch
                  :value="getExtraValue(extra)"
                  v-if="extra.extra_type !== 'free'">
                </CiSwitch>
                <CiCounter
                  :value="getExtraValue(extra)"
                  :canIncrease="getExtraValue(extra) < extraMaxAmount"
                  :canDecrease="getExtraValue(extra) > extraMinAmount"
                  @update:value="onExtraAmountChange(extra, $event)"
                  v-if="extra.extra_type === 'free'">
                </CiCounter>
              </div>
              <div slot="middle" class="extra__meta">
                <h3 class="meta__headline">{{ extra.name }}</h3>
                <div class="meta__description" v-html="extra.description"></div>
              </div>
              <div slot="right" class="extra__price">
                <template v-if="extra.billing_type === 'included' || extra.billing_type === 'per_consumption'">
                  <span class="price__meta price__meta--single">{{ $t(`generic.labels.${extra.billing_type}`) }}</span>
                </template>
                <template v-if="extra.billing_type !== 'included' && extra.billing_type !== 'per_consumption'">
                  <span class="price__value">{{ extra.price | currency(campground.currency, language.code) }}</span>
                  <span class="price__value price__value--alt" v-if="extra.currency_alt">{{ extra.currency_alt.price | currency(currency.code, language.code) }}</span>
                  <span class="price__meta">{{ $t(`generic.labels.${extra.billing_type}`) }}</span>
                </template>
              </div>
            </CiSelectionBox>
          </div>
        </div>
      </div>
    </div>

    <CiProgressNavigation
      :active-step="activeStep"
      :status="status"
      :steps="steps">
    </CiProgressNavigation>
  </div>
</template>

<script>
import bookingPriceMixin from '@/mixins/bookingPriceMixin'
import { mapGetters, mapActions } from 'vuex'
import {
  CiSelectionBox,
  CiInfoBox,
  CiCounter,
  CiSwitch,
  CiProgressNavigation,
} from '@/components/index'

import iconLock from '@/assets/icons/lock-solid.svg'

export default {
  name: 'CheckoutStepThree',
  mixins: [bookingPriceMixin],
  components: {
    CiCounter,
    CiInfoBox,
    CiProgressNavigation,
    CiSelectionBox,
    CiSwitch,
    iconLock,
  },
  data() {
    return {
      extraMaxAmount: 99,
      extraMinAmount: 0,
    }
  },
  created() {
    this.$snowplow.trackStep('extras_selection')
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
      steps: 'steps',
    }),

    ...mapGetters('booking', {
      price: 'price',
      selectedCampsite: 'selectedCampsite',
      selectedExtras: 'selectedExtras',
      selectedSeasons: 'selectedSeasons',
    }),

    ...mapGetters('campground', {
      availableExtras: 'availableExtras',
      campground: 'campground',
      status: 'status',
    }),

    getAvailableExtras() {
      // first get basic extra data based on id of available extras
      const mappedExtras = this.availableExtras.map(extra => {
        const basicExtra = this.campground.extras.find(ex => ex.id === extra.id)
        return { ...basicExtra, ...extra }
      })

      // filter extras which are not valid for selected campsite type
      return mappedExtras.filter(extra => {
        /* istanbul ignore else */
        if (this.selectedCampsite) {
          return extra[`applies_for_${this.selectedCampsite.campsite_type}`]
        }
      })
    },
  },
  methods: {
    ...mapActions('application', {
      setStepStatus: 'setStepStatus',
    }),

    ...mapActions('booking', {
      setExtra: 'setExtra',
    }),

    onExtraAmountChange(extra, newAmount) {
      /* istanbul ignore else */
      if (newAmount >= this.extraMaxAmount) newAmount = this.extraMaxAmount
      /* istanbul ignore else */
      if (newAmount <= this.extraMinAmount) newAmount = this.extraMinAmount
      this.setExtra({ extra, amount: newAmount })
    },

    getExtraValue: function(extra) {
      const ext = this.selectedExtras.find(e => e.id === extra.id)
      return ext ? ext.amount : 0
    },

    onExtraClicked(extra, event) {
      // if actual target is a link, open href in new tab
      if (event.target.tagName.toLowerCase() === 'a') {
        window.open(event.target.href, '_blank')
        event.preventDefault()
      } else {
        this.setExtra({ extra, amount: !this.getExtraValue(extra) })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.checkout-step-three__header {
  margin: 0 0 30px;
}

.checkout-step-three__headline {
  @include font-format(1.375rem, 1.625em);
}

.extra {
  margin: 0 0 20px;
}

.extra__status {
  display: flex;
  justify-content: flex-end;
  padding: 20px;

  @include media-breakpoint-up(lg) {
    justify-content: center;
  }

  .warning {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1rem);

    position: relative;
    background-color: rgba(248, 231, 28, 0.15);
    border-radius: 8px;
    color: #f5a623;
    padding: 8px 32px 6px 12px;
    text-transform: uppercase;

    .icon {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(-10px, -50%);
      width: 11px;
      height: auto;
      fill: #f5a623;
    }
  }
}

.extra__meta {
  border-bottom: 1px solid hsla(0, 0%, 60.8%, 0.5);
  flex: 3;
  padding: 20px;

  @include media-breakpoint-up(lg) {
    border: 0;
    padding: 0;
  }

  .meta__description {
    margin: 0;
    padding: 0;
  }
}

.extra__price {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  padding: 20px;

  @include media-breakpoint-up(lg) {
    align-items: center;
    padding: 0;
  }

  .price__value {
    @include font('gotham_mediumregular');
    @include font-format(1.375rem, 1.625em);

    &.price__value--alt {
      @include font-format(1rem, 1.25em);

      color: #9b9b9b;
      margin: 0 0 5px 0;
    }
  }

  .price__meta {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1.625em);

    color: #9b9b9b;
    margin: 0 10px 0 0;

    &.price__meta--single {
      @include font-format(1rem, 1rem);

      color: black;
    }
  }
}
</style>
